/**
 * PnlCompactTable — Kompakt P&L tablosu (Karar Destek Paneli).
 *
 * 2026-05 (faz 2): Bucket satırları inline expand olur — sayfa değişmez.
 *   Click 1: bucket → altta kategori listesi (Operasyon → Lojistik 37K, Bakım 40K, Sarf 452K)
 *   Click 2: kategori → altta bireysel gider kayıtları (lazy load /api/finance/giderler)
 *   Click 3: "Tümünü gör" linki → ilgili drill sayfasına götürür
 *
 * Komisyonlar görünür satır (anayasa madde 1).
 * Saf bilgi: AI sebep/çözüm yorumu YOK.
 *
 * Props:
 *   data: _ebitda_donem_hesapla output (kategori_detay dahil)
 *   range: { start, end } — gider kayıtları için tarih filtresi
 *   firma: string ('' | TORUNA_KASAP | INGA_BURGER | ALTI_USTU_KOFTE)
 *   onDrill: (path) => void — KPI/komisyon drill için
 */
import React, { useState } from 'react';
import { useTheme } from '../../styles/ThemeProvider';
import { formatTRY, formatPct } from '../../utils/analytics/format';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const ROW_PADDING = '6px 12px';

// Bucket → kullanıcıya gösterilecek isim (yalnız fallback; asıl kaynak backend
// opex_bucket_meta — M.1). Etiketler backend OPEX_BUCKET_META ile hizalı.
const BUCKET_LABEL = {
  PERSONNEL: 'Personel',
  RENT: 'Kira',
  UTILITIES: 'Enerji & Telekom',
  MARKETING: 'Pazarlama',
  OPERATIONS: 'Operasyon',
  G_AND_A: 'Genel Yönetim',
  OTHER_OPEX: 'Diğer',
};

// Kategori kod → kullanıcıya gösterilecek isim (master tablodan da gelebilir, fallback)
const KATEGORI_LABEL_FALLBACK = {
  PERSONEL: 'Personel',
  KIRA_STOPAJ: 'Resmi Ödemeler',
  ENERJI: 'Enerji',
  TELEKOMUNIKASYON: 'Telekomünikasyon',
  PAZARLAMA_REKLAM: 'Pazarlama ve Reklam',
  DIJITAL_SOSYAL_MEDYA: 'Dijital ve Sosyal Medya',
  LOJISTIK: 'Lojistik',
  BAKIM_ONARIM: 'Bakım ve Onarım',
  SARF_TEMIZLIK: 'Sarf ve Temizlik',
  TEMSIL_AGIRLAMA: 'Temsil ve Ağırlama',
  GENEL_YONETIM: 'Genel Yönetim Giderleri',
  DEMIRBAS: 'Demirbaş',
  DIGER: 'Diğer',
  BANKA_KART_KOMISYON: 'Banka Kart Komisyonu',
  YEMEK_KARTI_KOMISYON: 'Yemek Kartı Komisyonu',
  ONLINE_PLATFORM_KOMISYON: 'Online Platform Komisyonu',
  FINANSMAN: 'Finansman',
};

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function PnlCompactTable({ data, range, firma, onDrill }) {
  const { theme } = useTheme();
  const [expandedBuckets, setExpandedBuckets] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [categoryRecords, setCategoryRecords] = useState({}); // { kategori_kod: [kayitlar] }
  const [loadingCategory, setLoadingCategory] = useState({});

  if (!data) {
    return <div style={{ padding: theme.space.md, color: theme.color.textSoft }}>P&L verisi yok.</div>;
  }

  const navigate = (path) => () => { if (onDrill) onDrill(path); };

  const netCiroBrut = data.net_ciro_brut || 0;
  const komisyon = data.komisyon || {};
  const komisyonToplam = komisyon.TOPLAM || 0;
  const opex = data.opex || {};
  const opexToplam = data.opex_toplam || 0;
  const kategoriDetay = data.kategori_detay || []; // [{kategori, kategori_ad, bucket, tutar}]
  // OPEX bucket başlıkları backend opex_bucket_meta'dan (DB-driven, Anayasa M.1);
  // yoksa hardcoded BUCKET_LABEL fallback. Sıra da backend'den gelir — etiket/bucket
  // değişince frontend kod değişmeden yansır (örn. RENT = 'Kira', birleşik değil).
  const opexBucketMeta = (Array.isArray(data.opex_bucket_meta) && data.opex_bucket_meta.length)
    ? data.opex_bucket_meta
    : Object.entries(BUCKET_LABEL).map(([kod, label]) => ({ kod, label }));

  const toggleBucket = (bucketKey) => {
    setExpandedBuckets((prev) => ({ ...prev, [bucketKey]: !prev[bucketKey] }));
  };

  const toggleCategory = async (kategoriKod) => {
    const isExpanding = !expandedCategories[kategoriKod];
    setExpandedCategories((prev) => ({ ...prev, [kategoriKod]: isExpanding }));

    // Lazy load: ilk açılışta kayıtları çek
    if (isExpanding && !categoryRecords[kategoriKod] && range) {
      setLoadingCategory((prev) => ({ ...prev, [kategoriKod]: true }));
      try {
        // Backend /api/finance/giderler endpoint'i baslangic_tarihi/bitis_tarihi
        // bekliyor (FastAPI Query alias yok). Yanlış param adı sessizce ignore
        // edilirdi → tüm kayıtlar dönüyordu (PR #35 sonrası bug, fix bu PR).
        let url = `${API}/api/finance/giderler?baslangic_tarihi=${range.start}&bitis_tarihi=${range.end}&kategori=${kategoriKod}&limit=100`;
        if (firma) url += `&firma=${firma}`;
        const res = await fetch(url, { headers: authHeaders() });
        if (res.ok) {
          const d = await res.json();
          const items = Array.isArray(d) ? d : d.kayitlar || d.items || [];
          setCategoryRecords((prev) => ({ ...prev, [kategoriKod]: items }));
        }
      } catch (e) {
        // sessiz fail; UI'da loading kalkar, kayıt yoksa "veri yok" gösterir
      } finally {
        setLoadingCategory((prev) => ({ ...prev, [kategoriKod]: false }));
      }
    }
  };

  // ── Yardımcı render fonksiyonları ─────────────────────────────────
  const Row = ({ label, value, pct, depth = 0, total = false, danger = false, success = false, onClick, note, expandIcon }) => {
    const color = total ? theme.color.text : (danger ? theme.color.danger : (success ? theme.color.success : theme.color.text));
    return (
      <tr
        onClick={onClick}
        style={{
          cursor: onClick ? 'pointer' : 'default',
          background: total ? theme.color.surfaceAlt : 'transparent',
          fontWeight: total ? 700 : 400,
          borderTop: total ? `2px solid ${theme.color.border}` : 'none',
          borderBottom: total ? `2px solid ${theme.color.border}` : `1px solid ${theme.color.border}`,
        }}
      >
        <td style={{ padding: ROW_PADDING, paddingLeft: 12 + depth * 16, color }}>
          {expandIcon && <span style={{ display: 'inline-block', width: 14, color: theme.color.textSoft, fontSize: 10 }}>{expandIcon}</span>}
          {label}
          {note && <span style={{ marginLeft: 8, fontSize: 10, color: theme.color.textSoft }}>{note}</span>}
        </td>
        <td style={{ padding: ROW_PADDING, textAlign: 'right', fontFamily: 'monospace', color }}>
          {formatTRY(value, { decimals: 0 })}
        </td>
        <td style={{ padding: ROW_PADDING, textAlign: 'right', fontFamily: 'monospace', color: theme.color.textSoft, fontSize: 12 }}>
          {pct !== undefined && pct !== null ? formatPct(pct) : ''}
        </td>
      </tr>
    );
  };

  // Bucket satırı + altında kategori listesi (expanded ise)
  // value parametresi: opex sözlüğünde olmayan bucket'lar için (D_AND_A, FINANCE — EBITDA sonrası)
  // dışarıdan açıkça verilebilir; verilmezse opex sözlüğünden okunur.
  const renderBucketBlock = (bucketKey, bucketLabel, value) => {
    const val = value !== undefined ? value : (opex[bucketKey] || 0);
    if (val === 0) return null;

    const isExpanded = !!expandedBuckets[bucketKey];
    // Bucket'a bağlı kategoriler (kategori_detay'dan filtre)
    const kategoriler = kategoriDetay
      .filter((kd) => kd.bucket === bucketKey)
      .sort((a, b) => b.tutar - a.tutar);

    const rows = [
      <Row
        key={`b-${bucketKey}`}
        label={bucketLabel}
        value={-val}
        pct={data.net_ciro > 0 ? -(val / data.net_ciro * 100) : 0}
        depth={1}
        onClick={() => toggleBucket(bucketKey)}
        expandIcon={isExpanded ? '▼' : '▶'}
      />,
    ];

    if (isExpanded) {
      // Bilgi notu: kaç kategori var
      rows.push(
        <tr key={`b-${bucketKey}-info`} style={{ background: theme.color.surfaceAlt }}>
          <td colSpan={3} style={{ padding: '4px 12px 4px 44px', fontSize: 10, color: theme.color.textSoft }}>
            {kategoriler.length} alt kategori
          </td>
        </tr>
      );

      kategoriler.forEach((kd) => {
        const katIsExpanded = !!expandedCategories[kd.kategori];
        // Ayarlar'da düzenlenen canlı ad öncelikli (M.1); yoksa (komisyon vb.
        // gider_kategorileri'nde olmayan kalemler) hardcoded fallback, son çare ham kod.
        const katLabel = kd.kategori_ad || KATEGORI_LABEL_FALLBACK[kd.kategori] || kd.kategori;
        rows.push(
          <Row
            key={`k-${kd.kategori}`}
            label={katLabel}
            value={-kd.tutar}
            pct={data.net_ciro > 0 ? -(kd.tutar / data.net_ciro * 100) : 0}
            depth={2}
            onClick={() => toggleCategory(kd.kategori)}
            expandIcon={katIsExpanded ? '▼' : '▶'}
          />
        );

        if (katIsExpanded) {
          if (loadingCategory[kd.kategori]) {
            rows.push(
              <tr key={`k-${kd.kategori}-loading`} style={{ background: theme.color.surfaceAlt }}>
                <td colSpan={3} style={{ padding: '6px 12px 6px 60px', fontSize: 12, color: theme.color.textSoft }}>
                  Kayıtlar yükleniyor…
                </td>
              </tr>
            );
          } else {
            const records = categoryRecords[kd.kategori] || [];
            if (records.length === 0) {
              rows.push(
                <tr key={`k-${kd.kategori}-empty`} style={{ background: theme.color.surfaceAlt }}>
                  <td colSpan={3} style={{ padding: '6px 12px 6px 60px', fontSize: 12, color: theme.color.textSoft }}>
                    Bu dönemde kayıt yok.
                  </td>
                </tr>
              );
            } else {
              records.slice(0, 30).forEach((r) => {
                const tarih = r.gider_tarihi || r.tarih || '?';
                const f = r.firma || '?';
                const kalem = r.kalem || '';
                const tutar = r.tutar || 0;
                rows.push(
                  <tr key={`r-${r.id}`} style={{ background: theme.color.surfaceAlt, borderBottom: `1px solid ${theme.color.border}` }}>
                    <td style={{ padding: '4px 12px 4px 60px', fontSize: 11, color: theme.color.textSoft }}>
                      <span style={{ fontFamily: 'monospace', color: theme.color.text }}>{tarih}</span>
                      {' · '}
                      <span style={{ fontWeight: 600 }}>{f}</span>
                      {kalem && <> · {kalem.slice(0, 60)}</>}
                    </td>
                    <td style={{ padding: '4px 12px', textAlign: 'right', fontFamily: 'monospace', fontSize: 11, color: theme.color.textSoft }}>
                      {formatTRY(tutar, { decimals: 0 })}
                    </td>
                    <td />
                  </tr>
                );
              });
              if (records.length > 30) {
                rows.push(
                  <tr key={`k-${kd.kategori}-more`} style={{ background: theme.color.surfaceAlt }}>
                    <td colSpan={3} style={{ padding: '4px 12px 4px 60px', fontSize: 10, color: theme.color.textSoft, fontStyle: 'italic' }}>
                      … {records.length - 30} kayıt daha (tümünü görmek için "Finans → Giderler" sayfası)
                    </td>
                  </tr>
                );
              }
            }
          }
        }
      });
    }

    return rows;
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <section style={{ background: theme.color.surface, padding: theme.space.lg, borderRadius: theme.radius.md, border: `1px solid ${theme.color.border}`, overflow: 'auto' }}>
      <h3 style={{ marginTop: 0, color: theme.color.text }}>Kâr-Zarar Dökümü</h3>
      <div style={{ fontSize: 11, color: theme.color.textSoft, marginBottom: 8 }}>
        ▶ ile gösterilen satırları tıklayarak detayları açabilirsiniz.
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${theme.color.border}`, color: theme.color.textSoft, fontSize: 11 }}>
            <th style={{ padding: ROW_PADDING, textAlign: 'left' }}>Kalem</th>
            <th style={{ padding: ROW_PADDING, textAlign: 'right' }}>Tutar (TL)</th>
            <th style={{ padding: ROW_PADDING, textAlign: 'right' }}>% Net Ciro</th>
          </tr>
        </thead>
        <tbody>
          <Row label="Brüt Net Ciro (POS)" value={netCiroBrut} pct={100} onClick={navigate('/analiz/satis')} />

          {/* KOMİSYON BLOĞU */}
          {komisyonToplam > 0 && (
            <>
              <tr><td colSpan={3} style={{ padding: '8px 12px 4px', fontSize: 11, color: theme.color.textSoft, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Komisyon Yükü</td></tr>
              <Row
                label="Banka Kart Komisyonu"
                value={-(komisyon.BANKA_KART || 0)}
                pct={netCiroBrut > 0 ? -((komisyon.BANKA_KART || 0) / netCiroBrut * 100) : 0}
                depth={1}
                onClick={navigate('/analiz/komisyon-detay')}
              />
              <Row
                label="Yemek Kartı Komisyonu"
                value={-(komisyon.YEMEK_KARTI || 0)}
                pct={netCiroBrut > 0 ? -((komisyon.YEMEK_KARTI || 0) / netCiroBrut * 100) : 0}
                depth={1}
                onClick={navigate('/analiz/komisyon-detay')}
              />
              <Row
                label="Online Platform Komisyonu"
                value={-(komisyon.ONLINE_PLATFORM || 0)}
                pct={netCiroBrut > 0 ? -((komisyon.ONLINE_PLATFORM || 0) / netCiroBrut * 100) : 0}
                depth={1}
                onClick={navigate('/analiz/komisyon-detay')}
              />
            </>
          )}

          <Row
            label="Net Ciro (Komisyon Sonrası)"
            value={data.net_ciro || 0}
            pct={netCiroBrut > 0 ? (data.net_ciro / netCiroBrut * 100) : 100}
            total
            note="komisyon dahil"
          />

          <Row
            label="SMM (Hammadde/Food Cost)"
            value={-(data.smm || 0)}
            pct={data.food_cost_pct ? -data.food_cost_pct : 0}
            danger={data.food_cost_pct > 35}
            onClick={navigate('/analiz/maliyet-varyans')}
          />

          <Row label="Brüt Kâr" value={data.brut_kar || 0} pct={data.brut_marj_pct} total />

          {/* OPEX bucket'ları — her biri inline expand */}
          <tr><td colSpan={3} style={{ padding: '8px 12px 4px', fontSize: 11, color: theme.color.textSoft, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>İşletme Giderleri</td></tr>
          {opexBucketMeta.flatMap((b) => renderBucketBlock(b.kod, b.label || BUCKET_LABEL[b.kod] || b.kod) || [])}

          <Row label="OPEX Toplamı" value={-opexToplam} pct={data.opex_pct ? -data.opex_pct : 0} />

          {/* Fire/zayi + ikram-iptal: OPEX'e gömülmez, açık zarar satırları
              (ebitda = brüt − opex − fire − ikram; 2026-06-11 kullanıcı kararı) */}
          {(data.fire_zayi || 0) > 0 && (
            <Row
              label="Fire & Zayi"
              value={-data.fire_zayi}
              pct={data.net_ciro > 0 ? -(data.fire_zayi / data.net_ciro * 100) : 0}
              depth={1}
            />
          )}
          {(data.ikram_iptal_maliyet || 0) > 0 && (
            <Row
              label="İkram & İptal Maliyeti"
              value={-data.ikram_iptal_maliyet}
              pct={data.net_ciro > 0 ? -(data.ikram_iptal_maliyet / data.net_ciro * 100) : 0}
              depth={1}
              note="bedelsiz tüketim — kasaya girmeyen ürün maliyeti"
            />
          )}

          {/* Tahmini işçilik+vergi tahakkuku — KPI'lardan düşülen tutar tabloda da
              GÖRÜNÜR satır; EBITDA/Net Kâr alanları backend'den düşülmüş gelir
              (KPI=tablo tek gerçek, 2026-06-11 tutarsızlık fix'i). */}
          {(data.tahakkuk_tahmini_tl || 0) > 0 && (
            <Row
              label="Tahmini İşçilik+Vergi Tahakkuku"
              value={-data.tahakkuk_tahmini_tl}
              pct={data.net_ciro > 0 ? -(data.tahakkuk_tahmini_tl / data.net_ciro * 100) : 0}
              depth={1}
              note="bordro kapanınca kesinleşir"
            />
          )}

          <Row
            label="EBITDA"
            value={data.ebitda || 0}
            pct={data.ebitda_marj_pct}
            total
            danger={data.ebitda < 0}
            success={data.ebitda_marj_pct > 15}
          />

          {(data.d_and_a > 0 || data.finansman > 0 || (data.hesaplanan_kdv_tl || 0) > 0) && (
            <>
              {/* 2026-05-23: D_AND_A ve FINANCE bucket'ları artık expand'lı —
                  kategori_detay'da yer alan kategoriler (DEMIRBAS, FINANSMAN vb.) ve
                  bireysel gider kayıtları drill-down ile açılabiliyor. Önceden düz Row'du. */}
              {renderBucketBlock('D_AND_A', 'Amortisman (D&A)', data.d_and_a || 0) || []}
              {renderBucketBlock('FINANCE', 'Finansman', data.finansman || 0) || []}
              {/* Hesaplanan KDV (satış) — net kârdan düşülür (kullanıcı kararı
                  2026-06-23). EBITDA/EBIT etkilenmez; net_kar backend'de zaten
                  düşülmüş gelir (KPI=tablo tek gerçek). */}
              {(data.hesaplanan_kdv_tl || 0) > 0 && (
                <>
                  <Row
                    label={`Hesaplanan KDV (Satış %${data.kdv_orani_pct ?? ''}${!firma ? ' karma' : ''})`}
                    value={-data.hesaplanan_kdv_tl}
                    pct={data.net_ciro_brut > 0 ? -(data.hesaplanan_kdv_tl / data.net_ciro_brut * 100) : 0}
                    depth={1}
                    note="satış (çıkış) KDV'si — bilgi"
                  />
                  <Row
                    label="(−) İndirilecek KDV (alış)"
                    value={data.indirilecek_kdv_tl || 0}
                    pct={data.net_ciro_brut > 0 ? (data.indirilecek_kdv_tl || 0) / data.net_ciro_brut * 100 : 0}
                    depth={1}
                    note="malzeme alış KDV'si — bilgi"
                  />
                  <Row
                    label="= Net Ödenecek KDV"
                    value={-(data.net_kdv_tl || 0)}
                    pct={data.net_ciro_brut > 0 ? -((data.net_kdv_tl || 0) / data.net_ciro_brut * 100) : 0}
                    depth={1}
                    note="net kârdan düşülür (Hesaplanan − İndirilecek)"
                  />
                  {(data.devreden_kdv_tl || 0) > 0 && (
                    <Row
                      label="Devreden KDV (ileri taşınır)"
                      value={data.devreden_kdv_tl}
                      depth={2}
                      note="indirilecek > hesaplanan — sonraki döneme devreder"
                    />
                  )}
                </>
              )}
              <Row label="Net Kâr" value={data.net_kar || 0} pct={data.net_marj_pct} total
                   danger={data.net_kar < 0} success={data.net_marj_pct > 10} />
              {data.vergi_sonrasi_net_kar != null && (
                <>
                  <Row
                    label={`Kurumlar Vergisi Karşılığı (Tahmini %${data.kurumlar_vergisi_orani_pct ?? ''})`}
                    value={-(data.kurumlar_vergisi_tl || 0)}
                    pct={data.net_ciro_brut > 0 ? -((data.kurumlar_vergisi_tl || 0) / data.net_ciro_brut * 100) : 0}
                    depth={1}
                    note="tahmini gelir vergisi — net kârdan düşülür"
                  />
                  <Row label="Vergi Sonrası Net Kâr" value={data.vergi_sonrasi_net_kar}
                       pct={data.net_ciro_brut > 0 ? (data.vergi_sonrasi_net_kar / data.net_ciro_brut * 100) : 0}
                       total danger={data.vergi_sonrasi_net_kar < 0}
                       success={data.vergi_sonrasi_net_kar > 0} />
                </>
              )}
            </>
          )}
        </tbody>
      </table>
    </section>
  );
}
